import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader } from "../ui";
import { autoMatch } from "./boletos-lote/shared";
import { LoteUploadCard } from "./boletos-lote/LoteUploadCard";
import { LoteRowsTable } from "./boletos-lote/LoteRowsTable";
export default function AdminBoletosLote() {
  const [associados, setAssociados] = useState([]);
  const [commonTitle, setCommonTitle] = useState("");
  const [commonDescription, setCommonDescription] = useState("");
  const [commonBoletoType, setCommonBoletoType] = useState("Mensalidade");
  const [sendEmail, setSendEmail] = useState(true);
  const [rows, setRows] = useState([]); // { file, associate_id, status, error, title, valor, due_date }
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get("/admin/associados")
      .then((r) => setAssociados(r.data.filter((u) => u.status === "ativo")))
      .catch(() => toast.error("Falha ao carregar associados"));
  }, []);

  const associadosSorted = useMemo(
    () => [...associados].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [associados]
  );

  const onFilesPicked = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const newRows = list.map((f) => {
      const matched = autoMatch(f.name, associados);
      return {
        id: `${f.name}__${f.size}__${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        associate_id: matched,
        status: "idle", // idle | sending | ok | error
        error: "",
        title: "",
      };
    });
    setRows((prev) => [...prev, ...newRows]);
    setDone(false);
    e.target.value = "";
  };

  const removeRow = (id) => setRows((r) => r.filter((x) => x.id !== id));

  const setRowField = (id, field, value) =>
    setRows((r) => r.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  const clearAll = () => {
    setRows([]);
    setDone(false);
  };

  const allAssigned = rows.length > 0 && rows.every((r) => r.associate_id);
  const canSubmit = rows.length > 0 && allAssigned && !busy;

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Selecione o associado para cada boleto antes de enviar.");
      return;
    }
    setBusy(true);
    setDone(false);
    const assocMap = Object.fromEntries(associados.map((a) => [a.id, a]));

    for (const row of rows) {
      if (row.status === "ok") continue; // skip already-sent
      setRows((r) =>
        r.map((x) => (x.id === row.id ? { ...x, status: "sending", error: "" } : x))
      );
      const assoc = assocMap[row.associate_id];
      const titlePrefix = (commonTitle || "").trim();
      const rowTitle = (row.title || "").trim();
      const finalTitle =
        rowTitle ||
        (titlePrefix
          ? `${titlePrefix} — ${assoc?.name || row.file.name}`
          : row.file.name.replace(/\.[^.]+$/, ""));

      const fd = new FormData();
      fd.append("title", finalTitle);
      fd.append("description", (commonDescription || "").trim());
      fd.append("tag", "Boleto");
      fd.append("assigned_to", row.associate_id);
      fd.append("file", row.file);
      fd.append("send_email", sendEmail ? "true" : "false");
      if (row.valor) fd.append("valor", row.valor.toString().replace(",", "."));
      if (row.due_date) fd.append("due_date", row.due_date);
      if (commonBoletoType) fd.append("boleto_type", commonBoletoType);

      try {
        const res = await api.post("/admin/files", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const es = res.data?.email_status || null;
        setRows((r) =>
          r.map((x) =>
            x.id === row.id ? { ...x, status: "ok", email_status: es } : x
          )
        );
      } catch (e) {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || "Erro ao enviar";
        setRows((r) =>
          r.map((x) =>
            x.id === row.id ? { ...x, status: "error", error: msg } : x
          )
        );
      }
    }

    setBusy(false);
    setDone(true);
    setRows((current) => {
      const ok = current.filter((r) => r.status === "ok").length;
      const err = current.filter((r) => r.status === "error").length;
      // Only count e-mail as delivered when send_email was requested for the
      // batch AND the backend reported sent > 0 for the row. When SMTP failed
      // (email_status.failed > 0 with sent = 0) we must NOT show a green toast
      // — that was the whole confusion that led users to think everything
      // was delivered when actually only saved.
      const emailedRows = current.filter(
        (r) => (r.email_status?.sent || 0) > 0
      ).length;
      const emailFailedRows = current.filter(
        (r) =>
          r.status === "ok" &&
          (r.email_status?.sent || 0) === 0 &&
          (r.email_status?.failed || 0) > 0
      ).length;

      if (err === 0 && emailFailedRows === 0 && sendEmail && emailedRows === ok) {
        toast.success(`${ok} boleto(s) enviado(s) e e-mail entregue ao servidor.`);
      } else if (err === 0 && !sendEmail) {
        toast.success(`${ok} boleto(s) salvo(s). Envio de e-mail estava desativado.`);
      } else if (err === 0 && emailFailedRows > 0) {
        toast.warning(
          `${ok} boleto(s) salvo(s), mas o SMTP recusou o envio em ${emailFailedRows}. Verifique Configurações → E-mail.`
        );
      } else if (err > 0 && ok === 0) {
        toast.error(`Falha ao enviar ${err} boleto(s). Revise abaixo.`);
      } else {
        toast.warning(
          `${ok} boleto(s) salvo(s), ${err} com erro` +
            (emailFailedRows > 0
              ? ` · SMTP recusou envio em ${emailFailedRows}`
              : "") +
            ". Revise abaixo."
        );
      }
      return current;
    });
  };

  const summary = useMemo(() => {
    return {
      total: rows.length,
      assigned: rows.filter((r) => r.associate_id).length,
      ok: rows.filter((r) => r.status === "ok").length,
      err: rows.filter((r) => r.status === "error").length,
    };
  }, [rows]);

  return (
    <div data-testid="admin-boletos-lote">
      <PageHeader
        title="Upload de boletos em lote"
        subtitle="Selecione múltiplos PDFs de boleto e vincule cada arquivo ao associado correspondente. Ao enviar, cada boleto é anexado ao e-mail financeiro do associado."
        actions={
          <Link
            to="/admin/arquivos"
            className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 text-[11px] uppercase tracking-[0.22em] hover:border-slate-400"
          >
            <ArrowLeft size={14} /> Arquivos
          </Link>
        }
      />

      <LoteUploadCard associados={associados} commonBoletoType={commonBoletoType} commonDescription={commonDescription} commonTitle={commonTitle} onFilesPicked={onFilesPicked} sendEmail={sendEmail} setCommonBoletoType={setCommonBoletoType} setCommonDescription={setCommonDescription} setCommonTitle={setCommonTitle} setSendEmail={setSendEmail} />
      <LoteRowsTable associados={associados} associadosSorted={associadosSorted} busy={busy} canSubmit={canSubmit} clearAll={clearAll} done={done} removeRow={removeRow} rows={rows} setRowField={setRowField} submit={submit} summary={summary} />
    </div>
  );
}

